import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';

class Store extends EventDrivenStore {
  constructor() {
    super('orders', {
      list: {
        items: [],
        loading: false,
        error: null
      },
      detail: {
        order: null,
        loading: false,
        error: null
      },
      tracking: {
        status: null,
        currentLocation: null,
        rider: null,
        estimatedArrival: null,
        loading: false,
        error: null
      },
      checkout: {
        initiated: false,
        loading: false,
        error: null
      }
    });

    this.setupEvents();
  }

  setupEvents() {
    // ============ LIST EVENTS ============
    this.on(constants.EVENTS.ORDERS.LOADING, () => {
      this.state.list.loading = true;
      this.state.list.error = null;
    });

    this.on(constants.EVENTS.ORDERS.LOADED, (data) => {
      this.state.list.items = Array.isArray(data) ? data : [];
      this.state.list.loading = false;
      this.state.list.error = null;
    });

    this.on(constants.EVENTS.ORDERS.LOAD_ERROR, (error) => {
      this.state.list.loading = false;
      this.state.list.error = error?.message || error;
      this.state.list.items = [];
    });

    // ============ CREATE EVENTS ============
    this.on(constants.EVENTS.ORDERS.CREATING, () => {
      this.state.checkout.loading = true;
      this.state.checkout.error = null;
    });

    this.on(constants.EVENTS.ORDERS.CREATED, (order) => {
      this.state.detail.order = order;
      this.state.checkout.loading = false;
      this.state.checkout.error = null;
      this.state.checkout.initiated = false;
    });

    this.on(constants.EVENTS.ORDERS.CREATE_ERROR, (error) => {
      this.state.checkout.loading = false;
      this.state.checkout.error = error?.message || error;
    });

    // ============ DETAIL EVENTS ============
    this.on(constants.EVENTS.ORDERS.DETAIL_LOADING, () => {
      this.state.detail.loading = true;
      this.state.detail.error = null;
    });

    this.on(constants.EVENTS.ORDERS.DETAIL_LOADED, (order) => {
      this.state.detail.order = order;
      this.state.detail.loading = false;
      this.state.detail.error = null;
    });

    this.on(constants.EVENTS.ORDERS.DETAIL_ERROR, (error) => {
      this.state.detail.loading = false;
      this.state.detail.error = error?.message || error;
      this.state.detail.order = null;
    });

    // ============ TRACKING EVENTS ============
    this.on(constants.EVENTS.ORDERS.TRACKING, () => {
      this.state.tracking.loading = true;
      this.state.tracking.error = null;
    });

    this.on(constants.EVENTS.ORDERS.TRACKED, (trackingData) => {
      this.state.tracking.status = trackingData.status;
      this.state.tracking.currentLocation = trackingData.currentLocation;
      this.state.tracking.rider = trackingData.rider;
      this.state.tracking.estimatedArrival = trackingData.estimatedArrival;
      this.state.tracking.distanceRemaining = trackingData.distanceRemaining;
      this.state.tracking.durationRemaining = trackingData.durationRemaining;
      this.state.tracking.loading = false;
      this.state.tracking.error = null;
    });

    this.on(constants.EVENTS.ORDERS.TRACK_ERROR, (error) => {
      this.state.tracking.loading = false;
      this.state.tracking.error = error?.message || error;
    });

    // ============ CHECKOUT EVENTS ============
    this.on(constants.EVENTS.CHECKOUT.INITIATING, () => {
      this.state.checkout.loading = true;
      this.state.checkout.error = null;
    });

    this.on(constants.EVENTS.CHECKOUT.INITIATED, (checkoutData) => {
      this.state.checkout.initiated = true;
      this.state.checkout.loading = false;
      this.state.checkout.error = null;
    });

    this.on(constants.EVENTS.CHECKOUT.INITIATE_ERROR, (error) => {
      this.state.checkout.loading = false;
      this.state.checkout.error = error?.message || error;
    });

    this.on(constants.EVENTS.CHECKOUT.VALIDATING, () => {
      this.state.checkout.loading = true;
      this.state.checkout.error = null;
    });

    this.on(constants.EVENTS.CHECKOUT.VALIDATED, () => {
      this.state.checkout.loading = false;
      this.state.checkout.error = null;
    });

    this.on(constants.EVENTS.CHECKOUT.VALIDATE_ERROR, (error) => {
      this.state.checkout.loading = false;
      this.state.checkout.error = error?.message || error;
    });

    // ============ CANCEL EVENTS ============
    this.on(constants.EVENTS.ORDERS.CANCELLING, () => {
      this.state.detail.loading = true;
      this.state.detail.error = null;
    });

    this.on(constants.EVENTS.ORDERS.CANCELLED, (order) => {
      if (order && order.id === this.state.detail.order?.id) {
        this.state.detail.order = { ...this.state.detail.order, ...order, canCancel: false };
      }
      this.state.detail.loading = false;
    });

    this.on(constants.EVENTS.ORDERS.CANCEL_ERROR, (error) => {
      this.state.detail.loading = false;
      this.state.detail.error = error?.message || error;
    });

    // ============ RATING EVENTS ============
    this.on(constants.EVENTS.ORDERS.RATING, () => {
      this.state.detail.loading = true;
      this.state.detail.error = null;
    });

    this.on(constants.EVENTS.ORDERS.RATED, (rating) => {
      if (this.state.detail.order) {
        this.state.detail.order.rating = rating;
        this.state.detail.order.canRate = false;
      }
      this.state.detail.loading = false;
    });

    this.on(constants.EVENTS.ORDERS.RATE_ERROR, (error) => {
      this.state.detail.loading = false;
      this.state.detail.error = error?.message || error;
    });
  }

  // Getters
  get orders() {
    return this.state.list.items;
  }

  get currentOrder() {
    return this.state.detail.order;
  }

  get tracking() {
    return this.state.tracking;
  }

  get isLoading() {
    return this.state.list.loading ||
           this.state.detail.loading ||
           this.state.tracking.loading ||
           this.state.checkout.loading;
  }

  get hasError() {
    return !!(this.state.list.error ||
              this.state.detail.error ||
              this.state.tracking.error ||
              this.state.checkout.error);
  }

  get activeOrders() {
    return this.state.list.items.filter(order =>
      !['delivered', 'cancelled'].includes(order.status)
    );
  }

  get completedOrders() {
    return this.state.list.items.filter(order =>
      ['delivered', 'cancelled'].includes(order.status)
    );
  }
}

export default Store;
